import type React from 'react';

import { Card, CardBody } from '@change/ds-core-ui/Card';
import { Link } from '@change/ds-core-ui/Link';

type Props = Readonly<{
	cardKey: string;
	href: string;
	onClick: () => void;
	/**
	 * True for tiled/cloned copies rendered only to fill out the marquee ring - keeps every
	 * petition announced, and tabbed to, exactly once via aria-hidden + tabIndex. Deliberately
	 * NOT `inert`: the loop slides these copies into view, so they must stay clickable.
	 */
	duplicate?: boolean;
	children: React.ReactNode;
}>;

/** The frame a marquee card sits in: the list item, its fixed width, and the card link. */
export const CardWrapper: React.FC<Props> = ({ cardKey, href, onClick, duplicate = false, children }) => (
	// Spacing is a trailing margin (not a track gap) so the -50% loop point lands exactly
	// between two cards, and the fixed width is what the focus snap measures.
	<li className="me-4 w-80 shrink-0" aria-hidden={duplicate || undefined}>
		{/* The whole card is the link: a real <a> gives keyboard activation, open-in-new-tab and
		    link semantics for free, and routes client-side via the DS RouterProvider. */}
		<Card
			as={Link}
			href={href}
			onClick={onClick}
			underline={false}
			tabIndex={duplicate ? -1 : undefined}
			className="hover:elevation-low bg-background-base !text-typography-strong hover:!text-typography-strong visited:!text-typography-strong h-full cursor-pointer items-start rounded-lg [--card-padding:12px] focus-visible:rounded-lg"
			data-testid="petition-marquee-card"
			data-qa={`petition-marquee-card-${cardKey}`}
		>
			<CardBody className="flex h-full grow flex-col text-left">{children}</CardBody>
		</Card>
	</li>
);
